
import { formatDateISO } from 'src/utils/date';
import { hasRightAccess } from 'src/services/Subscription/access';
import { DeclarationTypes } from 'src/constants/declarations';
import { Subscription, SubscriptionType } from 'src/constants/subscription';
import { DashboardWidgetType, ListCustomsBasesSource, type DashboardWidgetConfig, type DashboardWidgetInstance, type LatestNomenclatureNewsWidgetConfig, type ListCustomsBasesWidgetConfig, type ListDeclarationsWidgetConfig, type OfficialJournalWidgetConfig, type WidgetDefinition, type WidgetSubtypeDefinition, type WidgetSubtypeOption } from 'types/app/dashboard';

const GRID_COLUMNS = 12;

export const WIDGET_NAME_MAX_LENGTH = 40;

export const LIST_CUSTOMS_BASES_SOURCE_SUBTYPES: WidgetSubtypeDefinition<ListCustomsBasesSource>[] = [
  {
    value: ListCustomsBasesSource.RECENT,
    labelKey: 'dashboard.list_customs_bases_source_recent'
  },
  {
    value: ListCustomsBasesSource.AUDITED,
    labelKey: 'dashboard.list_customs_bases_source_audited',
    tier: Subscription.AUDIT,
    subscriptionType: SubscriptionType.CUSTOMS_BASE
  },
  {
    value: ListCustomsBasesSource.MIXED,
    labelKey: 'dashboard.list_customs_bases_source_mixed',
    tier: Subscription.AUDIT,
    subscriptionType: SubscriptionType.CUSTOMS_BASE
  }
];

export function getWidgetDisplayTitle(options: { name?: string; label: string }): string {
  const trimmedName = options.name?.trim();
  if (trimmedName) return trimmedName;
  return options.label;
}

export function isWidgetSubtypeAccessible(
  subtype: Pick<WidgetSubtypeDefinition, 'tier' | 'subscriptionType'>
): boolean {
  if (!subtype.tier) return true;
  return hasRightAccess(subtype.subscriptionType ?? SubscriptionType.ANY, subtype.tier);
}

export function buildSubtypeOptions(
  definitions: WidgetSubtypeDefinition[],
  translate: (key: string) => string
): WidgetSubtypeOption[] {
  return definitions.map((definition) => ({
    ...definition,
    label: translate(definition.labelKey),
    disable: !isWidgetSubtypeAccessible(definition)
  }));
}

function createDefaultDateRange(monthsToLoad = 3): { from: string; to: string } {
  const from = new Date();
  from.setMonth(from.getMonth() - monthsToLoad);
  const to = new Date();
  to.setDate(to.getDate() + 1);
  return {
    from: formatDateISO(from) as string,
    to: formatDateISO(to) as string
  };
}

function createDefaultLookbackDateRange(daysToLoad = 7): { from: string; to: string } {
  const from = new Date();
  from.setDate(from.getDate() - daysToLoad);
  const to = new Date();
  return {
    from: formatDateISO(from) as string,
    to: formatDateISO(to) as string
  };
}

export function createDefaultListDeclarationsConfig(): ListDeclarationsWidgetConfig {
  return {
    declarationType: DeclarationTypes.DELTA_I,
    subtype: null,
    size: 5,
    dateRange: createDefaultDateRange(),
    filters: {
      searchText: '',
      status: null,
      tags: null,
      state: null,
      isDischargeable: false,
      companyId: null
    }
  };
}

export function createDefaultListCustomsBasesConfig(): ListCustomsBasesWidgetConfig {
  return {
    source: ListCustomsBasesSource.RECENT,
    size: 3
  };
}

export function createDefaultLatestNomenclatureNewsConfig(): LatestNomenclatureNewsWidgetConfig {
  return {
    dateFrom: createDefaultLookbackDateRange().from
  };
}

export function createDefaultOfficialJournalConfig(): OfficialJournalWidgetConfig {
  return {
    dateRange: createDefaultLookbackDateRange()
  };
}

export const AVAILABLE_WIDGETS: WidgetDefinition[] = [
  {
    type: DashboardWidgetType.LIST_DECLARATIONS,
    labelKey: 'dashboard.list_declarations_title',
    defaultWidth: 4,
    defaultHeight: 7,
    minWidth: 3,
    minHeight: 4,
    hasConfiguration: true,
    createDefaultConfig: createDefaultListDeclarationsConfig
  },
  {
    type: DashboardWidgetType.LIST_CUSTOMS_BASES,
    labelKey: 'dashboard.list_customs_bases_title',
    defaultWidth: 5,
    defaultHeight: 9,
    minWidth: 4,
    minHeight: 7,
    maxHeight: 7,
    hasConfiguration: true,
    createDefaultConfig: createDefaultListCustomsBasesConfig
  },
  {
    type: DashboardWidgetType.CUSTOMS_WEATHER,
    labelKey: 'dashboard.customs_weather_title',
    defaultWidth: 4,
    defaultHeight: 5,
    minWidth: 3,
    minHeight: 4,
    onlyOneInstance: true,
    hasConfiguration: false,
    createDefaultConfig: () => ({})
  },
  {
    type: DashboardWidgetType.LATEST_NOMENCLATURE_NEWS,
    labelKey: 'dashboard.latest_nomenclature_news_title',
    defaultWidth: 4,
    defaultHeight: 4,
    minWidth: 3,
    minHeight: 4,
    onlyOneInstance: true,
    hasConfiguration: true,
    createDefaultConfig: createDefaultLatestNomenclatureNewsConfig
  },
  {
    type: DashboardWidgetType.OFFICIAL_JOURNAL,
    labelKey: 'dashboard.official_journal_title',
    defaultWidth: 4,
    defaultHeight: 7,
    minWidth: 3,
    minHeight: 4,
    onlyOneInstance: true,
    hasConfiguration: true,
    createDefaultConfig: createDefaultOfficialJournalConfig
  },
  {
    type: DashboardWidgetType.PODCAST,
    labelKey: 'dashboard.podcast_title',
    defaultWidth: 4,
    defaultHeight: 5,
    minWidth: 4,
    minHeight: 5,
    onlyOneInstance: true,
    hasConfiguration: false,
    createDefaultConfig: () => ({})
  }
];

export function getWidgetDefinition(type: DashboardWidgetType): WidgetDefinition | undefined {
  return AVAILABLE_WIDGETS.find((widget) => widget.type === type);
}

export function canAddWidgetType(type: DashboardWidgetType, existingWidgets: DashboardWidgetInstance[]): boolean {
  const definition = getWidgetDefinition(type);
  if (!definition) return false;
  if (!definition.onlyOneInstance) return true;
  return !existingWidgets.some((widget) => widget.type === type);
}

function doRectanglesOverlap(
  first: { x: number; y: number; w: number; h: number },
  second: { x: number; y: number; w: number; h: number }
): boolean {
  return !(
    first.x + first.w <= second.x ||
    second.x + second.w <= first.x ||
    first.y + first.h <= second.y ||
    second.y + second.h <= first.y
  );
}

function isGridPlacementFree(
  existingWidgets: DashboardWidgetInstance[],
  candidate: { x: number; y: number; w: number; h: number }
): boolean {
  return !existingWidgets.some((widget) => doRectanglesOverlap(candidate, widget));
}

export function getNextWidgetPlacement(
  existingWidgets: DashboardWidgetInstance[],
  width: number,
  height: number
): { x: number; y: number; w: number; h: number } {
  if (existingWidgets.length === 0) {
    return { x: 0, y: 0, w: width, h: height };
  }

  const maxY = existingWidgets.reduce(
    (lowestFreeRow, widget) => Math.max(lowestFreeRow, widget.y + widget.h),
    0
  );

  for (let y = 0; y <= maxY; y++) {
    for (let x = 0; x <= GRID_COLUMNS - width; x++) {
      const candidate = { x, y, w: width, h: height };
      if (isGridPlacementFree(existingWidgets, candidate)) {
        return candidate;
      }
    }
  }

  return { x: 0, y: maxY, w: width, h: height };
}

export function createWidgetInstance(
  type: DashboardWidgetType,
  existingWidgets: DashboardWidgetInstance[],
  config?: DashboardWidgetConfig
): DashboardWidgetInstance | null {
  const definition = getWidgetDefinition(type);
  if (!definition) return null;
  if (!canAddWidgetType(type, existingWidgets)) return null;

  const placement = getNextWidgetPlacement(existingWidgets, definition.defaultWidth, definition.defaultHeight);

  return {
    id: `${type}-${crypto.randomUUID()}`,
    type,
    ...placement,
    config: config ? { ...config } : definition.createDefaultConfig()
  };
}
